'use client';

import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { generateAvatarFallback } from '@/utils/generateAvatarFallback';
import { formatNumber } from '@/utils/coins/formatNumber';
import { ArrowUp, ArrowDown, ChevronDown, Coins } from 'lucide-react';
import { useCoinPriceSocket } from '@/hooks/useCoinPriceSocket';
import { SymbolSelectorModal } from './SingleCoinTitle';
import { formatTomanPrice } from '@/utils/coins/bubble';
import { isUsdQuoted } from '@/utils/coins/quote';

export interface NavItem {
    id: string;
    label: string;
}

interface SingleCoinMobileNavProps {
    nameFa?: string;
    name?: string;
    symbol?: string;
    iconUrl?: string;
    price?: number | { price_buy?: number; ir_price_buy?: number; usdt_price?: number };
    /** Gold/currency quoted in dollars (the world gold ounce): the sticky bar shows the dollars */
    priceUsd?: number;
    quoteCurrency?: string;
    change?: number;
    hasExchange?: boolean;
    symbolType?: 'crypto' | 'gold' | 'currency' | 'forex';
    customNavItems?: NavItem[];
}

export const SingleCoinMobileNav: React.FC<SingleCoinMobileNavProps> = ({
    nameFa,
    name,
    symbol = '',
    iconUrl = '',
    price,
    priceUsd,
    quoteCurrency,
    change = 0,
    hasExchange = true,
    symbolType = 'crypto',
    customNavItems,
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [activeSection, setActiveSection] = useState<string>('chart-section');
    const [isSelectorOpen, setIsSelectorOpen] = useState<boolean>(false);
    const navScrollRef = useRef<HTMLDivElement>(null);

    const displayName = nameFa || name || '';

    const formatCurrentPrice = () => {
        let num = 0;
        if (typeof price === 'number') {
            num = price;
        } else if (typeof price === 'object') {
            num = price?.price_buy || price?.ir_price_buy || 0;
        }
        return num;
    };

    const isTomanAsset = symbolType === 'gold' || symbolType === 'currency';
    const usdQuoted =
        isTomanAsset && isUsdQuoted({ price_usd: priceUsd, quote_currency: quoteCurrency });
    // Gold & fiat are priced in toman; keep the sticky price live like the page header
    const { livePriceToman, livePriceUsdt } = useCoinPriceSocket(
        isTomanAsset ? symbol : '',
        usdQuoted ? priceUsd || 0 : 0,
        isTomanAsset ? formatCurrentPrice() : 0,
        isTomanAsset ? symbolType : 'crypto',
    );
    const currentPriceNum = usdQuoted
        ? livePriceUsdt || priceUsd || 0
        : isTomanAsset
          ? livePriceToman || formatCurrentPrice()
          : formatCurrentPrice();
    const isPositive = change >= 0;

    const navItems: NavItem[] = useMemo(() => {
        if (customNavItems && customNavItems.length > 0) {
            return customNavItems;
        }

        if (symbolType === 'forex') {
            return [
                { id: 'signal-section', label: 'سیگنال' },
                { id: 'chart-section', label: 'چارت' },
                { id: 'blog-content-section', label: 'مقاله' },
                { id: 'comments-section', label: 'نظرات' },
            ];
        }

        if (symbolType === 'gold' || symbolType === 'currency') {
            // Mobile render order:
            // 1. Chart (#chart-section)
            // 2. Live price list (#market-widget-section)
            // 3. Price & 24h change, mobile only (#market-table-section)
            // 3. Converter (#converter-section)
            // 4. Main Article & Intro (#blog-content-section)
            // 5. Comments (#comments-section)
            // 6. Related Posts (#related-section)
            return [
                { id: 'chart-section', label: 'نمودار' },
                { id: 'market-widget-section', label: 'قیمت‌ها' },
                { id: 'market-table-section', label: 'اطلاعات بازار' },
                { id: 'converter-section', label: 'مبدل' },
                { id: 'blog-content-section', label: 'معرفی و مقاله' },
                { id: 'comments-section', label: 'نظرات' },
                { id: 'related-section', label: 'مطالب مرتبط' },
            ];
        }

        // Default Crypto Nav Items (matching mobile order flex-col: order 1..10)
        // order-1: #chart-section
        // order-2: #exchanges-section
        // order-3: #about-section
        // order-4: #market-table-section
        // order-5: #sentiment-section
        // order-6: #prediction-section
        // order-7: #converter-section
        // order-8: #blog-content-section
        // order-9: #comments-section
        // order-10: #related-section
        return [
            { id: 'chart-section', label: 'نمودار' },
            ...(hasExchange ? [{ id: 'exchanges-section', label: 'صرافی‌ها' }] : []),
            { id: 'about-section', label: 'معرفی' },
            { id: 'market-table-section', label: 'بازار' },
            { id: 'sentiment-section', label: 'احساسات' },
            { id: 'prediction-section', label: 'پیش‌بینی' },
            { id: 'converter-section', label: 'مبدل' },
            { id: 'blog-content-section', label: 'مقاله' },
            { id: 'comments-section', label: 'نظرات' },
            { id: 'related-section', label: 'مرتبط' },
        ];
    }, [customNavItems, symbolType, hasExchange]);

    // Auto-scroll the nav strip to keep the active pill centered
    const scrollNavToActive = useCallback((sectionId: string) => {
        const container = navScrollRef.current;
        if (!container) return;
        const activeBtn = container.querySelector(
            `[data-nav-id="${sectionId}"]`,
        ) as HTMLElement | null;
        if (!activeBtn) return;
        const containerRect = container.getBoundingClientRect();
        const btnRect = activeBtn.getBoundingClientRect();
        const scrollLeft =
            container.scrollLeft +
            (btnRect.left - containerRect.left) -
            containerRect.width / 2 +
            btnRect.width / 2;
        container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            const scrollY = window.scrollY;
            if (scrollY > 120) {
                setIsVisible(true);
            } else {
                setIsVisible(false);
            }

            // Iterate in reverse so nested sections (e.g. comments inside blog-content) are detected first
            for (let i = navItems.length - 1; i >= 0; i--) {
                const item = navItems[i];
                const elem = document.getElementById(item.id);
                if (elem) {
                    const rect = elem.getBoundingClientRect();
                    if (rect.top <= 180 && rect.bottom >= 80) {
                        if (activeSection !== item.id) {
                            setActiveSection(item.id);
                            scrollNavToActive(item.id);
                        }
                        break;
                    }
                }
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [navItems, activeSection, scrollNavToActive]);

    const scrollToSection = (id: string) => {
        setActiveSection(id);
        const elem = document.getElementById(id);
        if (elem) {
            const yOffset = -90;
            const y = elem.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });
        }
    };

    return (
        <>
            <div
                // Lets the Tether Chand banner (same spot under the header) stay out of the way.
                className={`fixed top-12 left-0 right-0 z-40 lg:hidden bg-white/95 dark:bg-primary-active/95 backdrop-blur-xl border-b border-gray-200/80 dark:border-white/10 shadow-lg transition-all duration-300 px-3.5 py-2 ${
                    isVisible
                        ? 'translate-y-0 opacity-100 pointer-events-auto'
                        : '-translate-y-full opacity-0 pointer-events-none'
                }`}
            >
                {/* Symbol & Price Header Strip */}
                <div className='flex items-center justify-between gap-2 pb-1.5 border-b border-gray-100/80 dark:border-white/5'>
                    {/* Clickable Symbol Selector Button in Sticky Bar */}
                    <button
                        type='button'
                        onClick={() => setIsSelectorOpen(true)}
                        className='flex items-center gap-2 px-2.5 py-1 rounded-xl bg-gray-100/70 dark:bg-white/10 hover:bg-gray-200/80 dark:hover:bg-white/15 border border-gray-200/50 dark:border-white/10 active:scale-95 transition-all text-right cursor-pointer'
                    >
                        {symbolType === 'gold' || !iconUrl ? (
                            <span className='size-6 flex-shrink-0 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-500 dark:bg-amber-400/20 dark:text-amber-400 flex items-center justify-center'>
                                <Coins className='size-3.5' />
                            </span>
                        ) : (
                            <Avatar className='size-6 flex-shrink-0 border border-gray-200 dark:border-white/10 shadow-2xs'>
                                <AvatarImage src={iconUrl} alt={displayName} />
                                <AvatarFallback className='text-[10px] font-bold'>
                                    {generateAvatarFallback(displayName)}
                                </AvatarFallback>
                            </Avatar>
                        )}
                        <span className='font-bold text-xs text-gray-900 dark:text-white truncate max-w-[100px]'>
                            {displayName}
                        </span>
                        <ChevronDown className='size-3.5 text-gray-400 dark:text-gray-400 flex-shrink-0' />
                    </button>

                    {/* Price and Live Indicator Badge */}
                    <div className='flex items-center gap-1.5 text-xs font-bold'>
                        <div className='flex items-center gap-1 bg-gray-100/70 dark:bg-white/10 px-2 py-1 rounded-xl border border-gray-200/50 dark:border-white/10'>
                            <span
                                className={`size-1.5 rounded-full animate-pulse shrink-0 bg-emerald-500`}
                            />
                            {isTomanAsset && !usdQuoted ? (
                                <span className='text-gray-900 dark:text-white font-yekanBakh text-xs whitespace-nowrap'>
                                    <bdi dir='ltr'>{formatTomanPrice(currentPriceNum, symbol)}</bdi>
                                    <span className='mr-1 text-[10px] font-normal text-gray-500 dark:text-gray-400'>
                                        تومان
                                    </span>
                                </span>
                            ) : (
                                <span className='text-gray-900 dark:text-white dir-ltr font-mono text-xs'>
                                    ${formatNumber(currentPriceNum, false, true, 'USD', true)}
                                </span>
                            )}
                        </div>
                        <span
                            className={`inline-flex items-center gap-0.5 text-[11px] font-bold px-2 py-0.5 rounded-lg border ${
                                isPositive
                                    ? 'bg-emerald-500/10 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400 border-emerald-500/20'
                                    : 'bg-rose-500/10 text-rose-600 dark:bg-rose-500/20 dark:text-rose-400 border-rose-500/20'
                            }`}
                        >
                            {isPositive ? (
                                <ArrowUp className='size-3 stroke-[3]' />
                            ) : (
                                <ArrowDown className='size-3 stroke-[3]' />
                            )}
                            {Math.abs(change).toFixed(2)}%
                        </span>
                    </div>
                </div>

                {/* Quick Navigation Scrollable Section Pills */}
                <div
                    ref={navScrollRef}
                    className='flex items-center gap-1.5 overflow-x-auto no-scrollbar pt-2 text-xs scrollbar-none'
                >
                    {navItems.map((item) => {
                        const isActive = activeSection === item.id;
                        return (
                            <button
                                key={item.id}
                                data-nav-id={item.id}
                                onClick={() => scrollToSection(item.id)}
                                className={`px-3 py-1.5 rounded-xl whitespace-nowrap text-xs font-medium transition-all duration-200 cursor-pointer ${
                                    isActive
                                        ? 'bg-blue-600 dark:bg-blue-500 text-white font-bold shadow-xs scale-102'
                                        : 'bg-gray-100/80 dark:bg-white/5 text-gray-600 dark:text-gray-300 hover:bg-gray-200/80 dark:hover:bg-white/10'
                                }`}
                            >
                                {item.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Symbol Selector Drawer / Modal */}
            <SymbolSelectorModal
                isOpen={isSelectorOpen}
                onClose={() => setIsSelectorOpen(false)}
                currentSymbol={symbol}
                symbolType={symbolType}
            />
        </>
    );
};
